// @flow

import type {Language, ScheduledExport} from '@/types'

import React, {Component} from 'react'
import {connect} from 'react-redux'
import {evolve, map, mapObjIndexed, merge, pipe, values, keys, assoc, dissoc, drop, contains} from 'ramda'

import {appSelectors, formActions, portfoliosSelectors} from '@/store'
import {isMultipleEmail, nonEmptyString, validateObject} from '@/validation'

import CheckboxSelect from '@/controls/checkbox-select'
import EditField from '@/controls/edit-field'
import RadioSelect from '@/controls/radio-select'
import Selector from '@/controls/selector'
import DatePicker from '@/elements/date-picker'
import moment from 'moment'
import {prependIndexerString} from '@/store/portfolios/util'
import {dateFormatBasedOnCountry} from '@/defaults'

const toOption = (portfolio) => ({
  id: portfolio.portfolioId,
  name: portfolio.name,
})

const fileFormatOptions = [
  {
    name: 'Excel file (.xlsx)',
    value: 'xlsx',
  },
  {
    name: 'Excel 97-2003 file (.xls)',
    value: 'xls',
  },
  {
    name: 'Delimited text (.csv)',
    value: 'csv',
  },
]

const dataToExportOptions = [
  {name: 'All', disabled: false},
  {name: 'Safe number', disabled: false},
  {name: 'Company name', disabled: true},
  {name: 'Country code', disabled: true},
  {name: 'Company number', disabled: true},
  {name: 'Local rating', disabled: false, displayName: 'Local score'},
  {name: 'Rating', disabled: false, displayName: 'Score'},
  {name: 'Rating text', disabled: false, displayName: 'Score text'},
  {name: 'Status', disabled: false},
  {name: 'Credit limit', disabled: false},
  {name: 'Personal reference', disabled: false},
  {name: 'Personal limit', disabled: false},
  {name: 'Free text', disabled: false},
  {name: 'Turnover', disabled: false},
  {name: 'Address', disabled: false},
  {name: 'Number of employees', disabled: false},
  {name: 'Date of last change', disabled: false},
  {name: 'Summary', disabled: true},
  {name: 'Previous value', disabled: true},
  {name: 'New value', disabled: true},
  {name: 'Company Type', disabled: false},
]
const conditionalOptsForDataToExport = ['Summary', 'Previous value', 'New value']
const conditionalSectionsForSchedule = ['scheduleStartDate', 'schedule']
const defaultOptsForDataToExport = ['Company number', 'Company name', 'Country code']

const companiesToExportOptions = [
  {
    name: 'All companies',
  },
  {
    name: 'Changes since last alert email',
  },
  {
    name: 'Changes in last 24 hours',
  },
  {
    name: 'Changes in last week',
  },
  {
    name: 'Changes in last 2 weeks',
  },
  {
    name: 'Changes in last month',
  },
]

const format = {
  name: {
    label: 'Export Name',
    type: 'string',
    disableWhen: 'id',
    isRequired: true,
  },
  portfolio: {
    label: 'Portfolio(s)',
    type: 'select',
  },
  emailAddress: {
    label: 'Email address',
    type: 'string',
    helpText: 'You can add multiple emails separated by comma. example: abc@t.com,cba@t.com',
    isRequired: true,
  },
  exportType: {
    label: 'Export type',
    type: 'select',
    options: [
      {
        name: 'Immediate',
      },
      {
        name: 'Scheduled',
      },
    ],
  },
  scheduleStartDate: {
    label: 'Schedule start date',
    type: 'date',
    disableWhen: {
      exportType: 'Immediate',
    },
  },
  schedule: {
    label: 'Schedule',
    type: 'select',
    options: [
      {
        name: 'Daily',
      },
      {
        name: 'Weekly',
      },
      {
        name: 'Monthly',
      },
    ],
    disableWhen: {
      exportType: 'Immediate',
    },
  },
  fileFormat: {
    label: 'File format',
    type: 'select',
    options: fileFormatOptions,
  },
  companiesToExport: {
    label: 'Companies to export',
    type: 'select',
    options: companiesToExportOptions,
  },
  dataToExport: {
    label: 'Data to export',
    type: 'checkbox',
    helpTextPrefix: '*',
    helpText: 'All is recommended',
    options: dataToExportOptions,
    defaultOptions: [],
  },
}

type Props = {
  id: ?string,
  exports: {[id: string]: ScheduledExport},
  setFormData: (?Object) => void,
  l: Language,
  portfolios: Array<Object>,
  activePortfolioId: ?number,
  langCode: string,
}

const validators = {
  name: [nonEmptyString('Export Name')],
  emailAddress: [nonEmptyString('Email address'), isMultipleEmail('Email address', ',')],
}

const getDisabled = (props, state, {disableWhen}) => {
  switch (typeof disableWhen) {
    case 'string':
      if (state[disableWhen] || props[disableWhen]) {
        return true
      }
      return false
    case 'object':
      let isDisable = false
      keys(disableWhen).map((kei, index) => {
        isDisable = index === 0 ? (state[kei] === disableWhen[kei])
          : isDisable && state[kei] === disableWhen[kei]
      })
      return isDisable
    case 'boolean':
      return disableWhen
    default:
      return false
  }
}

const getSectionOptions = (props, state, section) => {
  if (section.type === 'radio') {
    return map((option) => {
      if (option.disableWhen) {
        const disabled = getDisabled(props, state, option)
        return assoc('disabled', disabled, dissoc('disableWhen', option))
      }
      return option
    }, section.options)
  }
  return section.options
}

const common = (k, update, props, state) => (section) => ({
  key: k,
  label: section.label,
  onChange: update(k),
  options: getSectionOptions(props, state, section),
  validators: validators[k],
  disable: getDisabled(props, state, section),
})

const toSelectorOptions = map(({id, value, name}) => ({key: (id || value || name), value: name}))

const getPortfoliosWithoutAllOption = (portfolios) => portfolios.filter((portfolio) => portfolio.portfolioId !== 0)

const renderElement = ({update, state, props}) => (section, k) => {
  if (k === 'portfolio') {
    section.options = map(toOption, getPortfoliosWithoutAllOption(props.portfolios))
  }
  if (k === 'companiesToExport') {
    let opt = 'Changes since last alert email'
    if (state.exportType === 'Scheduled') {
      section.options = section.options.filter((option) => {
        return option.name !== opt
      })
    } else {
      section.options = companiesToExportOptions
    }
  }
  // check if companies to export section - all selected or other
  if (k === 'dataToExport') {
    // if all selected
    if (state.companiesToExport === 'All companies') {
      const newOptions = section.options.filter((opt) => !conditionalOptsForDataToExport.includes(opt.name))
      section.options = newOptions
      section.defaultOptions = [...defaultOptsForDataToExport]
    } else {
      section.defaultOptions = [...defaultOptsForDataToExport, ...conditionalOptsForDataToExport]
      // check if conditionalOptsForDataToExport contains in options
      let containsSelectedOpts = false
      section.options.forEach((opt) => {
        if (opt.name === 'Summary') {
          containsSelectedOpts = true
        }
      })
      // if not present, add it to options
      if (!containsSelectedOpts) {
        conditionalOptsForDataToExport.forEach((opt) => section.options.push({name: opt, disabled: true}))
        section.options = section.options.filter((opt) => opt.name !== 'Company Type')
        section.options.push({name: 'Company Type', disabled: false})
      }
    }
  }
  const commonProps = common(k, update, props, state)
  if (!(state.exportType === 'Immediate' && conditionalSectionsForSchedule.includes(k))) {
    switch (section.type) {
      case 'radio':
        return <RadioSelect {...commonProps(section)} checked={state[k]} />
      case 'select':
        const selectSection = evolve({
          options: toSelectorOptions,
        }, section)
        return <Selector {...commonProps(selectSection)} selected={state[k]} />
      case 'checkbox':
        return <CheckboxSelect {...commonProps(section)} selected={state[k]} defaultOptions={section.defaultOptions} helpTextPrefix={section.helpTextPrefix} helpText={section.helpText}/>
      case 'string':
        return <EditField {...commonProps(section)} value={state[k]} helpText={section.helpText} isRequired={section.isRequired}/>
      case 'date':
        return <DatePicker {...commonProps(section)} value={state[k]} isEditMode={!!props.id}/>
      default:
        return null
    }
  }
}

const renderFormat = (deps) => pipe(
  mapObjIndexed(renderElement(deps)),
  values,
)

const formatKey = (key) => {
  key = key.replace(/[-_\s]+(.)?/g, (match, ch) =>
    (ch ? ch.toUpperCase() : '')
  )
  // Ensure first char is always lowercase
  return key.substr(0, 1).toLowerCase() + key.substr(1)
}

const dataToExportOptionsWithoutAll = drop(1, map((option) => option.name, dataToExportOptions))

const setDataToExportOptionsFormData = (options) => {
  let formattedData = {}
  map((option) => {
    formattedData[formatKey(option)] = contains(option, options)
  }, options)

  return formattedData
}

const setFormattedFormData = (data, props) => {
  const {dataToExport} = data
  let formattedData = {...data}
  formattedData = dissoc('dataToExport', formattedData)
  formattedData.portfolioId = formattedData.portfolio
  formattedData = dissoc('portfolio', formattedData)
  let dataToExportOptionsMap = {}

  if (dataToExport) {
    if (dataToExport[0] === 'All') {
      let options = []
      if (formattedData.companiesToExport !== 'All companies') {
        options = [...dataToExportOptionsWithoutAll]
      } else {
        options = dataToExportOptionsWithoutAll.filter((opt) => !conditionalOptsForDataToExport.includes(opt))
      }
      dataToExportOptionsMap = setDataToExportOptionsFormData(options)
    } else {
      dataToExportOptionsMap = setDataToExportOptionsFormData([...dataToExport, ...conditionalOptsForDataToExport])
    }
    formattedData = merge(formattedData, dataToExportOptionsMap)
  }
  // Remove schedule start date, frequency from form when export type is immediate.
  if (formattedData.exportType === 'Immediate') {
    formattedData = dissoc('scheduleStartDate', formattedData)
    formattedData = dissoc('schedule', formattedData)
  } else { // Format the dates what api expects
    formattedData.scheduleStartDate = moment(formattedData.scheduleStartDate,
      dateFormatBasedOnCountry(props.langCode)).format('YYYY-MM-DD')
  }

  props.setFormData(formattedData)
}

const toDataToExportArray = (exportObj) => {
  let isAll = true
  let dataToExportArray = []
  let filteredDataToExportArray = []
  if (exportObj.companiesToExport === 'All companies') {
    filteredDataToExportArray = dataToExportOptionsWithoutAll.filter((opt) => {
      return !conditionalOptsForDataToExport.includes(opt)
    })
  } else {
    filteredDataToExportArray = dataToExportOptionsWithoutAll
  }
  filteredDataToExportArray.reduce((accum, key) => {
    if (exportObj[formatKey(key)]) {
      accum.push(key)
    } else {
      isAll = false
    }
    return accum
  }, dataToExportArray)
  if (isAll) {
    dataToExportArray = ['All']
  }
  return dataToExportArray
}

const getFileFormat = (format) => {
  let fileFormat = 'xlsx'
  map((option) => {
    if (option.name === format) {
      fileFormat = option.value
    } else {
      fileFormat = format
    }
  }, fileFormatOptions)
  return fileFormat
}

export class RawNewExportForm extends Component<Props, *> {
  constructor (props: Props) {
    super(props)
    const stub = {
      portfolio: props.activePortfolioId,
      exportType: 'Immediate',
      companiesToExport: 'All companies',
      dataToExport: ['All'],
      fileFormat: 'xlsx',
      schedule: 'Daily',
      scheduleStartDate: moment().format(dateFormatBasedOnCountry(props.langCode)),
    }

    if (props.id) {
      const e = props.exports[prependIndexerString(props.id)]
      const fields = {
        name: e.name,
        portfolio: e.portfolioId,
        dataToExport: toDataToExportArray(e),
        companiesToExport: e.companiesToExport,
        exportType: e.exportType,
        schedule: (e.schedule && e.schedule !== 'null') ? e.schedule : 'Daily',
        scheduleStartDate: (e.scheduleStartDate && e.scheduleStartDate !== 'null')
          ? moment(e.scheduleStartDate).format(dateFormatBasedOnCountry(props.langCode)) : moment().format(dateFormatBasedOnCountry(props.langCode)),
        emailAddress: e.emailAddress,
        fileFormat: e.fileFormat ? getFileFormat(e.fileFormat) : 'xlsx',
      }
      this.state = merge(stub, fields)
    } else {
      this.state = stub
    }
  }

  update = (field: string) => (data: any) => {
    if (field === 'exportType' && data === 'Scheduled') {
      this.setState({
        [field]: data,
        'companiesToExport': 'All companies',
      }, () => {
        validateObject(validators, this.state)
          .fold(
            () => this.props.setFormData(null),
            (x) => setFormattedFormData(x, this.props)
          )
      })
    } else {
      this.setState({
        [field]: data,
      }, () => {
        validateObject(validators, this.state)
          .fold(
            () => this.props.setFormData(null),
            (x) => setFormattedFormData(x, this.props)
          )
      })
    }
  }

  render = () =>
    <form className="col-2 exports-form">
      {renderFormat(this)(format)}
    </form>
}

const mapStateToProps = (state) => ({
  id: state.form.id,
  portfolios: portfoliosSelectors.getPortfolios(state),
  activePortfolioId: appSelectors.getActivePortfolioId(state),
  exports: portfoliosSelectors.getActivePortfolioScheduledExportsMap(state),
  l: appSelectors.getL(state),
  langCode: state.app.user.preferredLanguageCode,
})

export default connect(
  mapStateToProps,
  formActions,
)(RawNewExportForm)



// WEBPACK FOOTER //
// ./src/views/forms/export.js